import React, { useRef } from 'react';
import { taraWritingOneResponse } from '../utils/taraWritingOneResponse';
import { useCollapseOnFirstFocus } from '../hooks/useCollapseOnFirstFocus';
import { useTmuaPearsonKeyboardInset } from '../hooks/TmuaPearsonKeyboardInsetContext';

const TOOLBAR_BUTTON_STYLE = (disabled) => ({
  background: '#FFFFFF',
  border: '1px solid #9A9A9A',
  borderRadius: 0,
  padding: '4px 12px',
  fontSize: 12,
  fontWeight: 600,
  color: disabled ? '#A0A0A0' : '#14161F',
  cursor: disabled ? 'default' : 'pointer',
});

/**
 * Pearson-styled writing editor for one expanded TARA Writing prompt item.
 * Structurally mirrors the cream essay canvas (grey Cut/Copy/Paste/Undo/Redo
 * bar, textarea, "Word Count : N" footer) but every write path is gated by
 * taraWritingOneResponse so a second prompt can never receive text while
 * another already holds a non-empty draft — including via Undo/Redo.
 */
export default function TmuaPearsonWritingEditor({
  promptText,
  textResponse,
  onChangeText,
  targetId,
  userAnswers,
  siblingIds,
  maxWords,
  onBlocked,
  textareaRef: externalTextareaRef,
}) {
  const ownTextareaRef = useRef(null);
  const textareaRef = externalTextareaRef || ownTextareaRef;
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const { collapsed: promptCollapsed, expand: expandPrompt, handleTextareaFocus: handlePromptCollapseOnFocus } = useCollapseOnFirstFocus();
  // Collapse-on-first-focus is mobile-only chrome behavior (desktop has
  // ample room and never needs to reclaim space from the prompt panel).
  const handleTextareaFocusMobileGated = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
      handlePromptCollapseOnFocus();
    }
  };
  const keyboardInset = useTmuaPearsonKeyboardInset();
  const keyboardOpen = keyboardInset > 0;

  const guardedWrite = (proposedText, { pushUndo, fromValue } = {}) => {
    const { block } = taraWritingOneResponse(proposedText, targetId, userAnswers, siblingIds);
    if (block) {
      if (onBlocked) onBlocked();
      return false;
    }
    if (pushUndo) {
      undoStackRef.current = [...undoStackRef.current.slice(-40), fromValue ?? textResponse ?? ''];
      redoStackRef.current = [];
    }
    onChangeText(proposedText);
    return true;
  };

  const handleChange = (event) => {
    guardedWrite(event.target.value, { pushUndo: true, fromValue: textResponse });
  };

  const handleCut = async () => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (start === end) return;
    const currentVal = el.value || '';
    const selected = currentVal.substring(start, end);
    try {
      await navigator.clipboard.writeText(selected);
    } catch {
      document.execCommand('copy');
    }
    const updated = currentVal.substring(0, start) + currentVal.substring(end);
    const wrote = guardedWrite(updated, { pushUndo: true, fromValue: currentVal });
    if (wrote) {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(start, start);
        }
      }, 0);
    }
  };

  const handleCopy = async () => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (start === end) return;
    const selected = (el.value || '').substring(start, end);
    try {
      await navigator.clipboard.writeText(selected);
    } catch {
      document.execCommand('copy');
    }
  };

  const handlePaste = async () => {
    const el = textareaRef.current;
    if (!el) return;
    let textToPaste = '';
    try {
      textToPaste = await navigator.clipboard.readText();
    } catch {
      textToPaste = '';
    }
    if (!textToPaste) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const currentVal = el.value || '';
    const updated = currentVal.substring(0, start) + textToPaste + currentVal.substring(end);
    const wrote = guardedWrite(updated, { pushUndo: true, fromValue: currentVal });
    if (wrote) {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const newPos = start + textToPaste.length;
          textareaRef.current.setSelectionRange(newPos, newPos);
        }
      }, 0);
    }
  };

  const handleUndo = () => {
    if (undoStackRef.current.length === 0) return;
    const prevVal = undoStackRef.current[undoStackRef.current.length - 1];
    const currentVal = textResponse || '';
    const { block } = taraWritingOneResponse(prevVal, targetId, userAnswers, siblingIds);
    if (block) {
      if (onBlocked) onBlocked();
      return;
    }
    undoStackRef.current = undoStackRef.current.slice(0, -1);
    redoStackRef.current = [...redoStackRef.current.slice(-40), currentVal];
    onChangeText(prevVal);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleRedo = () => {
    if (redoStackRef.current.length === 0) return;
    const nextVal = redoStackRef.current[redoStackRef.current.length - 1];
    const currentVal = textResponse || '';
    const { block } = taraWritingOneResponse(nextVal, targetId, userAnswers, siblingIds);
    if (block) {
      if (onBlocked) onBlocked();
      return;
    }
    redoStackRef.current = redoStackRef.current.slice(0, -1);
    undoStackRef.current = [...undoStackRef.current.slice(-40), currentVal];
    onChangeText(nextVal);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const wordCount = (textResponse || '').trim().split(/\s+/).filter(Boolean).length;
  const overLimit = maxWords && wordCount > maxWords;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <style>{`
        .tmua-pearson-writing-textarea {
          font-size: 15px;
        }
        @media (max-width: 768px) {
          .tmua-pearson-writing-textarea {
            font-size: 16px !important;
          }
        }
      `}</style>
      {!keyboardOpen ? (
        <div
          className={promptCollapsed ? 'tmua-pearson-writing-prompt-collapsed' : undefined}
          onClick={promptCollapsed ? expandPrompt : undefined}
          style={{
            flex: promptCollapsed ? 'none' : 1,
            minHeight: 0,
            maxHeight: promptCollapsed ? '3.2em' : 'none',
            overflowY: promptCollapsed ? 'hidden' : 'auto',
            padding: '20px 32px',
            cursor: promptCollapsed ? 'pointer' : 'default',
          }}
        >
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.5, color: '#000000', marginBottom: promptCollapsed ? 0 : 16 }}>
            {promptText}
          </div>
          {promptCollapsed ? (
            <span style={{ fontSize: 12, fontWeight: 700, color: '#737787' }}>Show more</span>
          ) : null}
        </div>
      ) : null}
      {!keyboardOpen ? (
        <div
          data-writing-keep-focus
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 32px',
            background: '#E8E8E8',
            borderTop: '1px solid #C7C7C7',
            borderBottom: '1px solid #C7C7C7',
          }}
        >
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleCut} style={TOOLBAR_BUTTON_STYLE(false)}>Cut</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleCopy} style={TOOLBAR_BUTTON_STYLE(false)}>Copy</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handlePaste} style={TOOLBAR_BUTTON_STYLE(false)}>Paste</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleUndo} style={TOOLBAR_BUTTON_STYLE(undoStackRef.current.length === 0)}>Undo</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleRedo} style={TOOLBAR_BUTTON_STYLE(redoStackRef.current.length === 0)}>Redo</button>
        </div>
      ) : null}
      <textarea
        ref={textareaRef}
        data-writing-keep-focus
        value={textResponse || ''}
        onChange={handleChange}
        onFocus={handleTextareaFocusMobileGated}
        className="tmua-pearson-writing-textarea"
        style={{
          flex: 2,
          minHeight: 0,
          resize: 'none',
          border: 'none',
          outline: 'none',
          padding: '16px 32px',
          lineHeight: 1.5,
          fontFamily: 'inherit',
          color: '#000000',
          background: '#FFFFFF',
        }}
      />
      <div
        style={{
          background: '#FFFFFF',
          borderTop: '1px solid #C7C7C7',
          padding: '10px 32px',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: overLimit ? '#8F3B27' : '#333333' }}>
          Word Count : {wordCount}
        </span>
      </div>
    </div>
  );
}
